import React, { useState, useEffect, useCallback } from "react";
import {
  UserPlus,
  UserMinus,
  Upload,
  Search,
  Edit,
  Trash2,
  Plus,
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  FileSpreadsheet,
  Phone,
  Mail,
  MapPin,
  Package,
  Download,
  Eye,
  Send,
  CheckSquare,
  Square,
  Filter,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Crown,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Info,
  UserRoundCog,
  Undo2,
} from "lucide-react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
// BEX address slice removed for Greek-CC (Greece uses ACS only)
// import { useAppDispatch, useAppSelector } from "../../store/hooks";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader, { TabConfig } from "../../components/PageHeader";
import { API_BASE_URL } from "../../utils/apiConfig";
import { formatPhoneWithLeadingZero } from "../../utils/phoneFormatter";
import { getLeadDisplayAddressColumn } from "../../utils/leadAddress";
import ImportErrorModal from "./ImportErrorModal";
import BulkSendErrorModal from "./BulkSendErrorModal";
import Toast, { ToastType } from "../../components/Toast";
import { useLanguage } from "../../contexts/LanguageContext";

// BEX types defined locally since slice was removed
type Municipality = { id: number; name: string };
type Settlement = { id: number; name: string; municipalityId: number; zip?: string };
type Street = { id: number; name: string; settlementId: number };

/** Format date string (YYYY-MM-DD or ISO) to dd-mm-yyyy. */
function formatNextCallDateDdMmYyyy(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  area?: string;
  street?: string;
  addr?: string;
  index?: string;
  notes?: string;
  stage?: 'new' | 'basic' | 'good' | 'potential' | 'customer' | 'vip';
  isProblematic?: boolean;
  problematicReason?: string;
  bexMatched?: boolean;
  bexMatchDetails?: {
    municipality?: string;
    settlement?: string;
    street?: string;
    addr?: string; // Address number (house/apartment number)
    confidence?: string;
  };
  matchingOrders?: Array<{
    orderId: number;
    status: number;
    date: Date | string;
  }>;
  stats?: {
    successfulOrders: number;
    totalOrders: number;
    totalSpent: number;
  };
  sentToCallCenter?: boolean;
  leadOwner?: {
    userId: string;
    username: string;
    email?: string;
    role: string;
    timestamp?: string;
  } | null;
  operatorStatus?: {
    operatorName: string;
    status: string;
    orderId?: number;
  } | null;
  callOutcome?: string;
  /** Set when operator rejected the order that needed confirmation; show "Unsuccessful confirmation" in Operator Status. */
  lastOrderRejected?: boolean;
  notInterestedReason?: string;
  callNotes?: string;
  followUpDate?: string;
  followUpHours?: string;
  followUpNotes?: string;
  followUpPotentialProduct?: string;
  callCenterCompletedAt?: string | Date;
  callCenterCompletedBy?: {
    userId: string;
    username: string;
    email?: string;
    role: string;
    timestamp?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface LeadsMainProps {
  currentUser?: {
    id: string;
    username?: string;
    email?: string;
    role: string;
    permissions?: string[];
  };
}

/** Set to true to show "Lead +" (Add Lead) button on the Leads page. */
const SHOW_ADD_LEAD = false;

const LeadsMain: React.FC<LeadsMainProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
  const [pageSize, setPageSize] = useState(50);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportResultsModal, setShowImportResultsModal] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [stageFilter, setStageFilter] = useState<string>("");
  const [problematicFilter, setProblematicFilter] = useState<string>("");
  const [problematicReasonFilter, setProblematicReasonFilter] = useState<string>("");
  const [operatorStatusFilter, setOperatorStatusFilter] = useState<string>("");
  const [notInterestedReasonFilter, setNotInterestedReasonFilter] = useState<string>(""); // Sub-filter when operator status is not_interested
  const [heldByOperatorFilter, setHeldByOperatorFilter] = useState<string>(""); // "" | "held" | "not_held" | userId
  const [inCallCenterFilter, setInCallCenterFilter] = useState<string>(""); // "" | "true" | "false"
  const [leadOwnersList, setLeadOwnersList] = useState<Array<{ userId: string; username: string }>>([]);
  const [lastOperatorSort, setLastOperatorSort] = useState<'default' | 'asc' | 'desc'>('default');
  const [filtersInitializedFromUrl, setFiltersInitializedFromUrl] = useState(false);
  const [sendingToCallCenter, setSendingToCallCenter] = useState<Set<string>>(new Set());
  const [gettingBackFromCallCenter, setGettingBackFromCallCenter] = useState<Set<string>>(new Set());
  const [takingLeadForProcess, setTakingLeadForProcess] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; type: ToastType; actionButton?: { label: string; onClick: () => void } } | null>(null);
  const [showBulkSendErrorModal, setShowBulkSendErrorModal] = useState(false);
  const [bulkSendErrors, setBulkSendErrors] = useState<Array<{ leadId: string; error: string; orderId?: number | null }>>([]);
  const [bulkSendStats, setBulkSendStats] = useState<{ sent: number; total: number }>({ sent: 0, total: 0 });
  const [showTaskSelectionModal, setShowTaskSelectionModal] = useState(false);
  const [pendingSendAction, setPendingSendAction] = useState<{ type: 'single' | 'bulk'; leadId?: string } | null>(null);
  const [showMoveToOperatorModal, setShowMoveToOperatorModal] = useState(false);
  const [moveToOperatorTargetId, setMoveToOperatorTargetId] = useState<string>("");
  const [moveToOperatorLoading, setMoveToOperatorLoading] = useState(false);
  const [movingBackFromCallCenter, setMovingBackFromCallCenter] = useState(false);
  const [callCenterOperators, setCallCenterOperators] = useState<Array<{ userId: string; username: string }>>([]);

  // Initialize filter state from URL (once on mount) so back from lead profile restores filters
  useEffect(() => {
    const page = searchParams.get("page");
    if (page != null) {
      const p = parseInt(page, 10);
      if (!Number.isNaN(p) && p >= 1) setCurrentPage(p);
    }
    const q = searchParams.get("q");
    if (q != null) setSearchTerm(q);
    const stage = searchParams.get("stage");
    if (stage != null) setStageFilter(stage);
    const prob = searchParams.get("prob");
    if (prob != null) setProblematicFilter(prob);
    const probReason = searchParams.get("probReason");
    if (probReason != null) setProblematicReasonFilter(probReason);
    const outcome = searchParams.get("outcome");
    if (outcome != null) setOperatorStatusFilter(outcome);
    const noInterest = searchParams.get("noInterest");
    if (noInterest != null) setNotInterestedReasonFilter(noInterest);
    const held = searchParams.get("held");
    const operatorId = searchParams.get("operatorId");
    if (held === "held" || held === "not_held") setHeldByOperatorFilter(held);
    else if (operatorId != null && operatorId !== "") setHeldByOperatorFilter(operatorId);
    const inCC = searchParams.get("inCC");
    if (inCC === "true" || inCC === "false") setInCallCenterFilter(inCC);
    const sortOp = searchParams.get("sortOp");
    if (sortOp === "asc" || sortOp === "desc") setLastOperatorSort(sortOp);
    const limit = searchParams.get("limit");
    if (limit != null) {
      const n = parseInt(limit, 10);
      if ([25, 50, 100, 200].includes(n)) setPageSize(n);
    }
    setFiltersInitializedFromUrl(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Update URL from filter handlers only (no effect) so URL updates immediately and we avoid extra re-renders/requests
  const updateUrlParams = useCallback((updates: Record<string, string | null | undefined>) => {
    setSearchParams((prev) => {
      const o: Record<string, string> = Object.fromEntries(prev.entries());
      for (const [k, v] of Object.entries(updates)) {
        if (v != null && v !== "") o[k] = v;
        else delete o[k];
      }
      return o;
    }, { replace: true });
  }, [setSearchParams]);

  const hasPermission = (permission: string): boolean => {
    return currentUser?.permissions?.includes(permission) || false;
  };

  const canView = hasPermission("leads.view");
  const canCreate = hasPermission("leads.create");
  const canEdit = hasPermission("leads.edit");
  const canDelete = hasPermission("leads.delete");

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      if (stageFilter) {
        params.append("stage", stageFilter);
      }

      if (problematicFilter) {
        params.append("problematic", problematicFilter);
      }

      if (problematicReasonFilter) {
        params.append("problematicReason", problematicReasonFilter);
      }

      if (operatorStatusFilter) {
        params.append("callOutcome", operatorStatusFilter);
      }
      if (operatorStatusFilter === "not_interested" && notInterestedReasonFilter) {
        params.append("notInterestedReason", notInterestedReasonFilter);
      }

      if (heldByOperatorFilter === "held") {
        params.append("hasLeadOwner", "true");
      } else if (heldByOperatorFilter === "not_held") {
        params.append("hasLeadOwner", "false");
      } else if (heldByOperatorFilter && heldByOperatorFilter !== "") {
        params.append("leadOwnerId", heldByOperatorFilter);
      }

      if (inCallCenterFilter) {
        params.append("inCallCenter", inCallCenterFilter);
      }

      if (lastOperatorSort !== 'default') {
        params.append("sortField", "callCenterCompletedAt");
        params.append("sortDirection", lastOperatorSort);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/leads?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setLeads(data.data.leads || []);
        setTotalPages(data.data.pagination?.totalPages || 1);
        setTotalCount(data.data.pagination?.totalCount || 0);
      } else {
        throw new Error(data.error || "Failed to fetch leads");
      }
    } catch (err) {
      console.error("Error fetching leads:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, [canView, currentPage, pageSize, searchTerm, stageFilter, problematicFilter, problematicReasonFilter, operatorStatusFilter, notInterestedReasonFilter, heldByOperatorFilter, inCallCenterFilter, lastOperatorSort]);

  // Fetch only after URL has been read (avoids double fetch: first with defaults, then with URL params)
  useEffect(() => {
    if (filtersInitializedFromUrl) fetchLeads();
  }, [filtersInitializedFromUrl, fetchLeads]);

  // Fetch distinct lead owners for filter dropdown (once per mount when canView; ref avoids duplicate calls from strict mode or effect re-runs)
  const leadOwnersFetchedRef = React.useRef(false);
  useEffect(() => {
    if (!canView || leadOwnersFetchedRef.current) return;
    leadOwnersFetchedRef.current = true;
    const token = localStorage.getItem("authToken");
    fetch(`${API_BASE_URL}/api/leads/lead-owners`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setLeadOwnersList(data.data);
        }
      })
      .catch(() => {});
  }, [canView]);

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    updateUrlParams({ q: value || null, page: "1" });
  };

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchLeads();
    setRefreshing(false);
  }, [fetchLeads]);

  // Build current filter params (same as fetchLeads, without page/limit) for export
  const getExportParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.append("search", searchTerm);
    if (stageFilter) params.append("stage", stageFilter);
    if (problematicFilter) params.append("problematic", problematicFilter);
    if (problematicReasonFilter) params.append("problematicReason", problematicReasonFilter);
    if (operatorStatusFilter) params.append("callOutcome", operatorStatusFilter);
    if (operatorStatusFilter === "not_interested" && notInterestedReasonFilter) params.append("notInterestedReason", notInterestedReasonFilter);
    if (heldByOperatorFilter === "held") params.append("hasLeadOwner", "true");
    else if (heldByOperatorFilter === "not_held") params.append("hasLeadOwner", "false");
    else if (heldByOperatorFilter) params.append("leadOwnerId", heldByOperatorFilter);
    if (inCallCenterFilter) params.append("inCallCenter", inCallCenterFilter);
    if (lastOperatorSort !== "default") {
      params.append("sortField", "callCenterCompletedAt");
      params.append("sortDirection", lastOperatorSort);
    }
    return params;
  }, [searchTerm, stageFilter, problematicFilter, problematicReasonFilter, operatorStatusFilter, notInterestedReasonFilter, heldByOperatorFilter, inCallCenterFilter, lastOperatorSort]);

  // Handle export filtered leads (CSV)
  const handleExport = useCallback(async () => {
    if (!canView) return;
    try {
      setExporting(true);
      const token = localStorage.getItem("authToken");
      const params = getExportParams();
      const response = await fetch(
        `${API_BASE_URL}/api/leads/export?${params.toString()}`,
        { credentials: "include", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      setError(err instanceof Error ? err.message : "Failed to export leads");
    } finally {
      setExporting(false);
    }
  }, [canView, getExportParams]);

  // Handle select all
  const handleSelectAll = () => {
    if (selectedLeads.size === leads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map(l => l.id)));
    }
  };

  // Handle select one
  const handleSelectOne = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  // Handle send to call center (single)
  const handleSendToCallCenter = async (leadId: string, task?: string) => {
    if (!canEdit) return;

    try {
      setSendingToCallCenter(prev => new Set(prev).add(leadId));
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/leads/${leadId}/send-to-call-center`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            task: task || null
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Navigate to call center
        navigate("/call-center");
        await fetchLeads();
      } else {
        throw new Error(data.error || "Failed to send to call center");
      }
    } catch (err) {
      console.error("Error sending to call center:", err);
      alert(err instanceof Error ? err.message : "Failed to send to call center");
    } finally {
      setSendingToCallCenter(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });
    }
  };

  const handleTakeLeadForProcess = async (leadId: string) => {
    if (!canEdit || !currentUser) return;
    try {
      setTakingLeadForProcess(prev => new Set(prev).add(leadId));
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          leadOwner: {
            userId: currentUser.id,
            username: currentUser.username ?? "",
            email: currentUser.email ?? "",
            role: currentUser.role,
            timestamp: new Date().toISOString(),
          },
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Failed to take lead");
      navigate(`/call-center?processLead=${leadId}`);
    } catch (err) {
      console.error("Error taking lead for process:", err);
      setToast({
        message: err instanceof Error ? err.message : "Failed to take lead",
        type: "error",
      });
    } finally {
      setTakingLeadForProcess(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  const handleGetBackFromCallCenter = async (leadId: string) => {
    if (!canEdit) return;
    try {
      setGettingBackFromCallCenter(prev => new Set(prev).add(leadId));
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ leadOwner: null, sentToCallCenter: false }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Failed to get back from call center");
      await fetchLeads();
      setToast({ message: t('leads.getBackFromCallCenterSuccess') || "Lead returned from call center", type: "success" });
    } catch (err) {
      console.error("Error getting back from call center:", err);
      setToast({
        message: err instanceof Error ? err.message : "Failed to get back from call center",
        type: "error",
      });
    } finally {
      setGettingBackFromCallCenter(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  // Handle bulk send to call center (leadIds = only leads not in CC when called from bulk bar)
  const handleBulkSendToCallCenter = async (task?: string, leadIds?: string[]) => {
    const ids = leadIds ?? Array.from(selectedLeads);
    if (!canEdit || ids.length === 0) return;

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/leads/bulk-send-to-call-center`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            leadIds: ids,
            task: task || null
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        const { sent, errors } = data.data;
        const total = ids.length;
        
        setBulkSendStats({ sent, total });
        
        // If there are errors, show them in modal
        if (errors && errors.length > 0) {
          setBulkSendErrors(errors);
          setShowBulkSendErrorModal(true);
        }
        
        // Show toast notification
        if (sent > 0 && errors.length === 0) {
          // All successful
          setToast({
            message: `Successfully sent ${sent} lead(s) to call center`,
            type: "success",
          });
          setSelectedLeads(new Set());
          await fetchLeads();
          setTimeout(() => {
            navigate("/call-center");
          }, 1500);
        } else if (sent > 0 && errors.length > 0) {
          // Partial success
          setToast({
            message: `Sent ${sent} lead(s) successfully, but ${errors.length} error(s) occurred`,
            type: "warning",
            actionButton: {
              label: "View Details",
              onClick: () => {
                setShowBulkSendErrorModal(true);
              },
            },
          });
          setSelectedLeads(new Set());
          await fetchLeads();
        } else {
          // All failed
          setToast({
            message: `Failed to send leads. ${errors.length} error(s) occurred`,
            type: "error",
            actionButton: {
              label: "View Details",
              onClick: () => {
                setShowBulkSendErrorModal(true);
              },
            },
          });
        }
      } else {
        throw new Error(data.error || "Failed to send to call center");
      }
    } catch (err) {
      console.error("Error bulk sending to call center:", err);
      setToast({
        message: err instanceof Error ? err.message : "Failed to send to call center",
        type: "error",
      });
    }
  };

  // Handle bulk move back from call center (only leads that are in call center)
  const handleBulkMoveBackFromCallCenter = async (leadIds: string[]) => {
    if (!canEdit || leadIds.length === 0) return;
    setMovingBackFromCallCenter(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/leads/bulk-move-back-from-call-center`, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ leadIds }),
      });
      const data = await response.json();
      if (data.success) {
        const { movedBack, errors } = data.data;
        setToast({
          message: t("leads.moveBackFromCallCenterSuccess", { count: movedBack }),
          type: errors.length > 0 ? "warning" : "success",
        });
        setSelectedLeads((prev) => {
          const next = new Set(prev);
          leadIds.forEach((id) => next.delete(id));
          return next;
        });
        await fetchLeads();
      } else {
        setToast({ message: data.error || "Failed to move leads back", type: "error" });
      }
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : "Failed to move leads back from call center",
        type: "error",
      });
    } finally {
      setMovingBackFromCallCenter(false);
    }
  };

  // Fetch call-center operators for Move to operator modal
  const fetchCallCenterOperators = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/api/leads/call-center-operators`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setCallCenterOperators(data.data);
      } else {
        setCallCenterOperators([]);
      }
    } catch {
      setCallCenterOperators([]);
    }
  }, []);

  // Handle bulk assign to operator (Move to other operator)
  const handleBulkAssignOperator = async () => {
    if (!canEdit || selectedLeads.size === 0 || !moveToOperatorTargetId) return;
    setMoveToOperatorLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/leads/bulk-assign-operator`, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          leadIds: Array.from(selectedLeads),
          operatorId: moveToOperatorTargetId,
        }),
      });
      const data = await response.json();
      if (data.success) {
        const { assigned } = data.data;
        setToast({
          message: t("leads.moveToOperatorSuccess", { count: assigned }),
          type: "success",
        });
        setShowMoveToOperatorModal(false);
        setMoveToOperatorTargetId("");
        setSelectedLeads(new Set());
        await fetchLeads();
      } else {
        setToast({
          message: data.error || "Failed to assign leads",
          type: "error",
        });
      }
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : "Failed to assign leads",
        type: "error",
      });
    } finally {
      setMoveToOperatorLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!canDelete || selectedLeads.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.size} lead(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/leads/bulk-delete`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            leadIds: Array.from(selectedLeads),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSelectedLeads(new Set());
        await fetchLeads();
        alert(`Successfully deleted ${data.data.deleted} lead(s).${data.data.errors.length > 0 ? ` ${data.data.errors.length} error(s) occurred.` : ''}`);
      } else {
        throw new Error(data.error || "Failed to delete leads");
      }
    } catch (err) {
      console.error("Error bulk deleting leads:", err);
      alert(err instanceof Error ? err.message : "Failed to delete leads");
    }
  };

  // Get stage badge
  const getStageBadge = (stage?: string) => {
    if (!stage) return null;
    const stages = {
      new: { label: "New", color: "bg-gray-100 text-gray-800" },
      basic: { label: "Basic", color: "bg-blue-100 text-blue-800" },
      good: { label: "Good", color: "bg-green-100 text-green-800" },
      potential: { label: "Potential", color: "bg-yellow-100 text-yellow-800" },
      customer: { label: "Customer", color: "bg-purple-100 text-purple-800" },
      vip: { label: "VIP", color: "bg-amber-100 text-amber-800" },
    };
    const stageInfo = stages[stage as keyof typeof stages] || stages.new;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${stageInfo.color}`}>
        {stage === 'vip' && <Crown className="w-3 h-3 inline mr-1" />}
        {stageInfo.label}
      </span>
    );
  };

  // Handle delete
  const handleDelete = async (leadId: string) => {
    if (!canDelete) {
      setError("You don't have permission to delete leads");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this lead?")) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchLeads();
      } else {
        throw new Error(data.error || "Failed to delete lead");
      }
    } catch (err) {
      console.error("Error deleting lead:", err);
      setError(err instanceof Error ? err.message : "Failed to delete lead");
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">
            You don't have permission to access Leads.
          </p>
        </div>
      </div>
    );
  }

  const customButtons = [
    ...(canCreate && SHOW_ADD_LEAD
      ? [
          {
            id: "add-lead",
            label: "Lead +",
            icon: Plus,
            onClick: () => setShowAddModal(true),
            variant: "primary" as const,
          },
        ]
      : []),
    ...(canCreate
      ? [
          {
            id: "import-csv",
            label: "Import CSV",
            icon: Upload,
            onClick: () => setShowImportModal(true),
            variant: "secondary" as const,
          },
        ]
      : []),

    {
      id: "export",
      label: t("leads.export"),
      icon: Download,
      onClick: handleExport,
      variant: "secondary" as const,
      disabled: exporting,
    },
    {
      id: "refresh",
      label: "Refresh",
      icon: RefreshCw,
      onClick: handleRefresh,
      variant: "secondary" as const,
      disabled: refreshing,
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('leads.title')}
        description={t('leads.title')}
        icon={UserPlus}
        customButtons={customButtons}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="inline-flex text-red-400 hover:text-red-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('message.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={stageFilter}
              onChange={(e) => {
                const v = e.target.value;
                setStageFilter(v);
                setCurrentPage(1);
                updateUrlParams({ stage: v || null, page: "1" });
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            >
              <option value="">{t('leads.allStages')}</option>
              <option value="new">{t('leads.new')}</option>
              <option value="basic">{t('leads.basic')}</option>
              <option value="good">{t('leads.good')}</option>
              <option value="potential">{t('leads.potential')}</option>
              <option value="customer">{t('leads.customer')}</option>
              <option value="vip">{t('leads.vip')}</option>
            </select>
          </div>
          <div className="flex flex-col gap-2 items-start">
            <div className="relative w-full min-w-0">
              <AlertCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={problematicFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setProblematicFilter(v);
                  if (v !== "true") setProblematicReasonFilter("");
                  setCurrentPage(1);
                  updateUrlParams({ prob: v || null, probReason: v === "true" ? problematicReasonFilter || null : null, page: "1" });
                }}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
              >
                <option value="">{t('leads.all')}</option>
                <option value="true">{t('leads.problematic')}</option>
                <option value="false">{t('leads.notProblematic')}</option>
              </select>
            </div>
            {problematicFilter === "true" && (
              <div className="relative w-full min-w-0">
                <AlertCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={problematicReasonFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    setProblematicReasonFilter(v);
                    setCurrentPage(1);
                    updateUrlParams({ probReason: v || null, page: "1" });
                  }}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent w-full"
                >
                  <option value="">{t('leads.allReasons')}</option>
                  <option value="address">{t('leads.reasonAddress')}</option>
                </select>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-start">
            <div className="relative w-full min-w-0">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={operatorStatusFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setOperatorStatusFilter(v);
                  if (v !== "not_interested") setNotInterestedReasonFilter("");
                  setCurrentPage(1);
                  updateUrlParams({ outcome: v || null, noInterest: v === "not_interested" ? notInterestedReasonFilter || null : null, page: "1" });
                }}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                aria-label={t('leads.operatorStatus')}
                title={t('leads.operatorStatus')}
              >
                <option value="">{t('leads.operatorStatus')}</option>
                <option value="successful">{t('leads.callOutcomeSuccessful')}</option>
                <option value="successful_with_confirmation">{t('leads.callOutcomeSuccessfulWithConfirmation')}</option>
                <option value="no_answer">{t('leads.callOutcomeNoAnswer')}</option>
                <option value="busy_line">{t('leads.busyLine')}</option>
                <option value="wrong_number">{t('leads.wrongNumber')}</option>
                <option value="unreachable">{t('leads.unreachable')}</option>
                <option value="number_doesnt_exist">{t('leads.numberDoesntExist')}</option>
                <option value="callback">{t('leads.callOutcomeCallback')}</option>
                <option value="not_interested">{t('leads.callOutcomeNotInterested')}</option>
                <option value="other">{t('leads.callOutcomeOther')}</option>
              </select>
            </div>
            {operatorStatusFilter === "not_interested" && (
              <div className="relative w-full min-w-0">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={notInterestedReasonFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNotInterestedReasonFilter(v);
                    setCurrentPage(1);
                    updateUrlParams({ noInterest: v || null, page: "1" });
                  }}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent w-full"
                  aria-label={t('leads.notInterestedReason')}
                  title={t('leads.notInterestedReason')}
                >
                  <option value="">{t('leads.allReasons')}</option>
                  <option value="do_not_call_again">{t('leads.notInterestedReasonDoNotCallAgain')}</option>
                <option value="no_money">{t('leads.notInterestedReasonNoMoney')}</option>
                <option value="still_has_product">{t('leads.notInterestedReasonStillHasProduct')}</option>
                <option value="product_doesnt_help">{t('leads.notInterestedReasonProductDoesntHelp')}</option>
                <option value="because_of_death">{t('leads.notInterestedReasonBecauseOfDeath')}</option>
                <option value="orders_for_someone_else">{t('leads.notInterestedReasonOrdersForSomeoneElse')}</option>
                <option value="not_interested_not_interested">{t('leads.notInterestedReasonNotInterested')}</option>
                <option value="other">{t('leads.notInterestedReasonOther')}</option>
              </select>
              </div>
            )}
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={heldByOperatorFilter}
              onChange={(e) => {
                const v = e.target.value;
                setHeldByOperatorFilter(v);
                setCurrentPage(1);
                if (v === "held" || v === "not_held") updateUrlParams({ held: v, operatorId: null, page: "1" });
                else updateUrlParams({ held: null, operatorId: v || null, page: "1" });
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
              aria-label={t('leads.heldByOperator')}
              title={t('leads.heldByOperator')}
            >
              <option value="">{t('leads.all')}</option>
              {leadOwnersList.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.username}
                </option>
              ))}
              <option value="held">{t('leads.heldByOperator')}</option>
              <option value="not_held">{t('leads.notHeldByOperator')}</option>
            </select>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={inCallCenterFilter}
              onChange={(e) => {
                const v = e.target.value;
                setInCallCenterFilter(v);
                setCurrentPage(1);
                updateUrlParams({ inCC: v || null, page: "1" });
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
              aria-label={t('leads.inCallCenter')}
              title={t('leads.inCallCenter')}
            >
              <option value="">{t('leads.all')}</option>
              <option value="true">{t('leads.inCallCenter')}</option>
              <option value="false">{t('leads.notInCallCenter')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar — minimal, no green background */}
      {selectedLeads.size > 0 && (() => {
        const selectedFromPage = leads.filter((l) => selectedLeads.has(l.id));
        const countNotInCC = selectedFromPage.filter((l) => !l.sentToCallCenter).length;
        const countInCC = selectedFromPage.filter((l) => l.sentToCallCenter === true).length;
        const idsInCC = selectedFromPage.filter((l) => l.sentToCallCenter === true).map((l) => l.id);
        return (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <span className="text-sm font-medium text-gray-700">
            {selectedLeads.size} {t("common.lead")}(s) {t("message.selected")}
          </span>
          <div className="h-4 w-px bg-gray-200" aria-hidden />
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <>
                {countNotInCC > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setPendingSendAction({ type: "bulk" });
                      setShowTaskSelectionModal(true);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:ring-offset-1"
                  >
                    <Send className="h-4 w-4" />
                    {t("leads.sendToCallCenter")} ({countNotInCC})
                  </button>
                )}
                {countInCC > 0 && (
                  <button
                    type="button"
                    onClick={() => handleBulkMoveBackFromCallCenter(idsInCC)}
                    disabled={movingBackFromCallCenter}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:ring-offset-1 disabled:opacity-50"
                  >
                    <Undo2 className="h-4 w-4" />
                    {t("leads.moveBackFromCallCenter")} ({countInCC})
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMoveToOperatorTargetId("");
                    fetchCallCenterOperators();
                    setShowMoveToOperatorModal(true);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:ring-offset-1"
                >
                  <UserRoundCog className="h-4 w-4" />
                  {t("leads.moveToOperator")}
                </button>
              </>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={handleBulkDelete}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              >
                <Trash2 className="h-4 w-4" />
                {t("leads.bulkDelete")}
              </button>
            )}
          </div>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSelectedLeads(new Set())}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
              title={t("common.clear") || "Clear selection"}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        );
      })()}

      {/* Move to other operator modal */}
      {showMoveToOperatorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-100 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("leads.moveToOperator")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t("leads.moveToOperatorDescription")}
              </p>
            </div>
            <div className="px-5 py-4">
              <label className="block text-sm font-medium text-gray-700">
                {t("leads.selectOperator")}
              </label>
              <select
                value={moveToOperatorTargetId}
                onChange={(e) => setMoveToOperatorTargetId(e.target.value)}
                className="mt-1.5 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-[#264a3e] focus:outline-none focus:ring-1 focus:ring-[#264a3e]"
              >
                <option value="">— {t("leads.selectOperator")} —</option>
                {callCenterOperators.map((op) => (
                  <option key={op.userId} value={op.userId}>
                    {op.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  if (!moveToOperatorLoading) {
                    setShowMoveToOperatorModal(false);
                    setMoveToOperatorTargetId("");
                  }
                }}
                disabled={moveToOperatorLoading}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleBulkAssignOperator}
                disabled={moveToOperatorLoading || !moveToOperatorTargetId}
                className="rounded-lg bg-[#264a3e] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3b32] disabled:opacity-50"
              >
                {moveToOperatorLoading ? (t("message.loading") || "Loading...") : t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm || stageFilter || problematicFilter || operatorStatusFilter || heldByOperatorFilter || inCallCenterFilter
                ? t('leads.noLeadsMatching')
                : t('leads.noLeadsFound')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={handleSelectAll}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {selectedLeads.size === leads.length ? (
                          <CheckSquare className="w-5 h-5" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ maxWidth: 200 }}>
                      {t('common.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.phone')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ maxWidth: 280 }}>
                      {t('common.address')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('leads.stage')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.orders')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('leads.operatorStatus')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => {
                          const next = lastOperatorSort === "default" ? "asc" : lastOperatorSort === "asc" ? "desc" : "default";
                          setLastOperatorSort(next);
                          setCurrentPage(1);
                          updateUrlParams({ sortOp: next === "default" ? null : next, page: "1" });
                        }}
                        className="inline-flex items-center gap-1 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:ring-offset-1 rounded"
                        title={lastOperatorSort === 'default' ? t('leads.sortByLastOperator') : lastOperatorSort === 'asc' ? t('leads.sortAz') : t('leads.sortZa')}
                      >
                        {t('leads.lastOperator')}
                        {lastOperatorSort === 'default' && <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />}
                        {lastOperatorSort === 'asc' && <ArrowUp className="w-3.5 h-3.5 text-[#264a3e]" />}
                        {lastOperatorSort === 'desc' && <ArrowDown className="w-3.5 h-3.5 text-[#264a3e]" />}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleSelectOne(lead.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {selectedLeads.has(lead.id) ? (
                            <CheckSquare className="w-5 h-5" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 max-w-[200px]" style={{ maxWidth: 200 }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="text-sm font-medium text-gray-900 break-words">
                            {lead.name}
                          </div>
                          {lead.isProblematic && (
                            <div className="flex flex-col gap-1">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                                title={`${t('leads.problematic')}: ${lead.problematicReason || 'address'}`}
                              >
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {t('leads.problematic')}
                              </span>
                              {lead.problematicReason && (
                                <span className="text-xs text-red-600 font-medium">
                                  {t(`leads.reason${lead.problematicReason.charAt(0).toUpperCase() + lead.problematicReason.slice(1)}`) || lead.problematicReason}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {lead.email && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {lead.email}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Phone className="w-4 h-4 text-gray-400 mr-2" />
                          {formatPhoneWithLeadingZero(lead.phone)}
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-[280px]" style={{ maxWidth: 280 }}>
                        <div className="flex items-start text-sm text-gray-900">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="break-words">
                            {(() => {
                              const addr = getLeadDisplayAddressColumn(lead);
                              return addr === "—" ? <span className="text-gray-400">—</span> : addr;
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStageBadge(lead.stage)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {lead.stats ? (
                          <div className="flex items-center text-sm">
                            <Package className="w-4 h-4 text-blue-500 mr-2" />
                            <span className="font-medium text-gray-900">
                              {lead.stats.totalOrders}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm" style={{ minWidth: "180px" }}>
                        <div className="flex items-center justify-between gap-x-2 min-w-0">
                          <span className="min-w-0 inline-flex flex-col gap-0.5">
                            {(lead.lastOrderRejected || lead.callOutcome) ? (
                              <>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit ${
                                  lead.lastOrderRejected ? 'bg-red-100 text-red-800' :
                                  lead.callOutcome === 'successful' ? 'bg-green-100 text-green-800' :
                                  lead.callOutcome === 'successful_with_confirmation' ? 'bg-amber-100 text-amber-800' :
                                  lead.callOutcome === 'no_answer' ? 'bg-yellow-100 text-yellow-800' :
                                  lead.callOutcome === 'busy_line' || lead.callOutcome === 'wrong_number' || lead.callOutcome === 'unreachable' || lead.callOutcome === 'number_doesnt_exist' ? 'bg-orange-100 text-orange-800' :
                                  lead.callOutcome === 'callback' ? 'bg-blue-100 text-blue-800' :
                                  lead.callOutcome === 'not_interested' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {lead.lastOrderRejected && t('leads.callOutcomeUnsuccessfulConfirmation')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'successful' && t('leads.callOutcomeSuccessful')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'successful_with_confirmation' && t('leads.callOutcomeSuccessfulWithConfirmation')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'no_answer' && t('leads.callOutcomeNoAnswer')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'busy_line' && t('leads.busyLine')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'wrong_number' && t('leads.wrongNumber')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'unreachable' && t('leads.unreachable')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'number_doesnt_exist' && t('leads.numberDoesntExist')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'callback' && t('leads.callOutcomeCallback')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'not_interested' && t('leads.callOutcomeNotInterested')}
                                  {!lead.lastOrderRejected && lead.callOutcome === 'other' && t('leads.callOutcomeOther')}
                                  {!lead.lastOrderRejected && !['successful', 'successful_with_confirmation', 'no_answer', 'busy_line', 'wrong_number', 'unreachable', 'number_doesnt_exist', 'callback', 'not_interested', 'other'].includes(lead.callOutcome || '') && lead.callOutcome}
                                </span>
                                {lead.callOutcome === 'not_interested' && lead.notInterestedReason && (
                                  <span className="text-xs text-gray-600">
                                    {lead.notInterestedReason === 'no_money' && t('leads.notInterestedReasonNoMoney')}
                                    {lead.notInterestedReason === 'still_has_product' && t('leads.notInterestedReasonStillHasProduct')}
                                    {lead.notInterestedReason === 'product_doesnt_help' && t('leads.notInterestedReasonProductDoesntHelp')}
                                    {lead.notInterestedReason === 'do_not_call_again' && t('leads.notInterestedReasonDoNotCallAgain')}
                                    {lead.notInterestedReason === 'because_of_death' && t('leads.notInterestedReasonBecauseOfDeath')}
                                    {lead.notInterestedReason === 'orders_for_someone_else' && t('leads.notInterestedReasonOrdersForSomeoneElse')}
                                    {lead.notInterestedReason === 'not_interested_not_interested' && t('leads.notInterestedReasonNotInterested')}
                                    {lead.notInterestedReason === 'other' && t('leads.notInterestedReasonOther')}
                                    {!['no_money', 'still_has_product', 'product_doesnt_help', 'do_not_call_again', 'because_of_death', 'orders_for_someone_else', 'not_interested_not_interested', 'other'].includes(lead.notInterestedReason) && lead.notInterestedReason}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </span>
                          {lead.callOutcome === "callback" && ((lead.followUpNotes && lead.followUpNotes.trim()) || (lead.followUpPotentialProduct && lead.followUpPotentialProduct.trim()) || lead.followUpDate) && (
                            <div className="relative inline-flex group items-center shrink-0 ml-auto">
                              <span className="inline-flex items-center justify-center w-4 h-4 max-w-[24px] max-h-[24px] rounded-full bg-blue-100 text-blue-600 cursor-help shrink-0">
                                <Info className="w-3 h-3" />
                              </span>
                              <div className="absolute left-full top-0 ml-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto z-30 w-52 min-w-52 px-2.5 py-2 text-xs rounded-lg shadow-lg border border-gray-200 bg-white text-left transition-opacity">
                                <div className="font-medium text-gray-600 pb-1 border-b border-gray-100">
                                  {t('callCenter.followUp')}
                                </div>
                                <div className="space-y-1 mt-1">
                                  {lead.followUpDate && (
                                    <div>
                                      <span className="text-gray-500">{t('callCenter.nextCallDate')}: </span>
                                      {formatNextCallDateDdMmYyyy(lead.followUpDate)}
                                      {lead.followUpHours ? ` ${lead.followUpHours}` : ""}
                                    </div>
                                  )}
                                  {lead.followUpNotes && lead.followUpNotes.trim() && (
                                    <div className="break-words">
                                      <span className="text-gray-500">{t('callCenter.followUpMessage')}: </span>
                                      {lead.followUpNotes.trim()}
                                    </div>
                                  )}
                                  {lead.followUpPotentialProduct && lead.followUpPotentialProduct.trim() && (
                                    <div className="break-words">
                                      <span className="text-gray-500">{t('callCenter.nextCallProduct')}: </span>
                                      {lead.followUpPotentialProduct.trim()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {lead.callCenterCompletedBy ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {lead.callCenterCompletedBy.username?.toUpperCase() ?? '—'}
                            </div>
                            {lead.callCenterCompletedAt && (
                              <div className="text-xs text-gray-500">
                                {new Date(lead.callCenterCompletedAt).toLocaleString()}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {selectedLeads.size === 0 && (
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/leads/${lead.id}`, { state: { fromListSearch: location.search } });
                              }}
                              className="text-[#264a3e] hover:text-[#1a3329]"
                              title={t('leads.viewDetails')}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {canEdit && lead.leadOwner && (
                              <button
                                onClick={() => handleGetBackFromCallCenter(lead.id)}
                                disabled={gettingBackFromCallCenter.has(lead.id)}
                                className="text-amber-600 hover:text-amber-800"
                                title={t('leads.takeFromCallOperator')}
                              >
                                {gettingBackFromCallCenter.has(lead.id) ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <UserMinus className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            {canEdit && !lead.leadOwner && (
                              <button
                                onClick={() => {
                                  if (window.confirm(t('leads.confirmProcessLead'))) {
                                    handleTakeLeadForProcess(lead.id);
                                  }
                                }}
                                disabled={takingLeadForProcess.has(lead.id)}
                                className="text-[#264a3e] hover:text-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed"
                                title={t('leads.editLead')}
                              >
                                {takingLeadForProcess.has(lead.id) ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Edit className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            {canEdit && !lead.leadOwner && lead.sentToCallCenter && (
                              <button
                                onClick={() => handleGetBackFromCallCenter(lead.id)}
                                className="text-red-800 hover:text-red-900"
                                title={t('leads.getBackFromCallCenter')}
                              >
                                {gettingBackFromCallCenter.has(lead.id) ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4 rotate-180" />
                                )}
                              </button>
                            )}
                            {canEdit && !lead.leadOwner && !lead.sentToCallCenter && (
                              <button
                                onClick={() => {
                                  setPendingSendAction({ type: 'single', leadId: lead.id });
                                  setShowTaskSelectionModal(true);
                                }}
                                disabled={sendingToCallCenter.has(lead.id)}
                                className="text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={t('leads.sendToCallCenter')}
                              >
                                {sendingToCallCenter.has(lead.id) ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(lead.id)}
                                className="text-red-600 hover:text-red-800"
                                title={t('leads.deleteLead')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2 order-2 sm:order-1">
                    <span className="text-sm text-gray-700">
                      {t('message.showing')} {totalCount === 0 ? 0 : ((currentPage - 1) * pageSize) + 1} {t('message.to')}{" "}
                      {Math.min(currentPage * pageSize, totalCount)} {t('message.of')} {totalCount}{" "}
                      {t('common.leads')}
                    </span>
                    <label className="flex items-center gap-1.5 text-sm text-gray-700">
                      <span>{t('leads.pageSize')}</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setPageSize(value);
                          setCurrentPage(1);
                          updateUrlParams({ limit: String(value), page: "1" });
                        }}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                        aria-label={t('leads.pageSize')}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="flex items-center flex-wrap justify-center gap-1 order-1 sm:order-2">
                    <button
                      onClick={() => { setCurrentPage(1); updateUrlParams({ page: "1" }); }}
                      disabled={currentPage === 1}
                      className="p-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      title={t('common.first')}
                      aria-label={t('common.first')}
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { const p = Math.max(1, currentPage - 1); setCurrentPage(p); updateUrlParams({ page: p.toString() }); }}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      {t('common.previous')}
                    </button>
                    {(() => {
                      const pageNumbers: (number | "…")[] = [];
                      const start = Math.max(1, Math.min(currentPage - 1, totalPages - 4));
                      const end = Math.min(totalPages, start + 4);
                      if (start > 1) {
                        pageNumbers.push(1);
                        if (start > 2) pageNumbers.push("…");
                      }
                      for (let i = start; i <= end; i++) pageNumbers.push(i);
                      if (end < totalPages) {
                        if (end < totalPages - 1) pageNumbers.push("…");
                        pageNumbers.push(totalPages);
                      }
                      return pageNumbers.map((p, idx) =>
                        p === "…" ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => { setCurrentPage(p as number); updateUrlParams({ page: String(p) }); }}
                            className={`min-w-[2rem] px-2 py-1.5 rounded-md text-sm border ${
                              currentPage === p
                                ? "bg-[#264a3e] text-white border-[#264a3e]"
                                : "border-gray-300 hover:bg-gray-100"
                            }`}
                            aria-current={currentPage === p ? "page" : undefined}
                          >
                            {p}
                          </button>
                        )
                      );
                    })()}
                    <button
                      onClick={() => { const p = Math.min(totalPages, currentPage + 1); setCurrentPage(p); updateUrlParams({ page: p.toString() }); }}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      {t('common.next')}
                    </button>
                    <button
                      onClick={() => { setCurrentPage(totalPages); updateUrlParams({ page: totalPages.toString() }); }}
                      disabled={currentPage === totalPages}
                      className="p-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      title={t('common.last')}
                      aria-label={t('common.last')}
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Lead Modal */}
      {(showAddModal || editingLead) && (
        <AddEditLeadModal
          isOpen={true}
          onClose={() => {
            setShowAddModal(false);
            setEditingLead(null);
          }}
          onSave={async () => {
            await fetchLeads();
            setShowAddModal(false);
            setEditingLead(null);
          }}
          lead={editingLead || undefined}
        />
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <ImportCSVModal
          isOpen={true}
          onClose={() => setShowImportModal(false)}
          onImport={async () => {
            await fetchLeads();
          }}
          onSuccess={(result) => {
            setImportResult(result);
            setShowImportModal(false);
            setShowImportResultsModal(true);
          }}
        />
      )}

      {/* Import Results Modal (after import completes) */}
      <ImportResultsModal
        isOpen={showImportResultsModal}
        result={importResult}
        onClose={() => {
          setShowImportResultsModal(false);
          setImportResult(null);
        }}
      />

      {/* Bulk Send Error Modal */}
      <BulkSendErrorModal
        isOpen={showBulkSendErrorModal}
        onClose={() => setShowBulkSendErrorModal(false)}
        errors={bulkSendErrors}
        sent={bulkSendStats.sent}
        total={bulkSendStats.total}
      />

      {/* Task Selection Modal */}
      {showTaskSelectionModal && (
        <TaskSelectionModal
          isOpen={true}
          onClose={() => {
            setShowTaskSelectionModal(false);
            setPendingSendAction(null);
          }}
          onConfirm={async (task: string) => {
            if (pendingSendAction) {
              if (pendingSendAction.type === 'bulk') {
                const idsToSend = leads.filter((l) => selectedLeads.has(l.id) && !l.sentToCallCenter).map((l) => l.id);
                await handleBulkSendToCallCenter(task, idsToSend.length > 0 ? idsToSend : undefined);
              } else if (pendingSendAction.type === 'single' && pendingSendAction.leadId) {
                await handleSendToCallCenter(pendingSendAction.leadId, task);
              }
            }
            setShowTaskSelectionModal(false);
            setPendingSendAction(null);
          }}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          actionButton={toast.actionButton}
        />
      )}
    </div>
  );
};

// Task Selection Modal Component
interface TaskSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (task: string) => void;
}

const TaskSelectionModal: React.FC<TaskSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [customTask, setCustomTask] = useState<string>("");

  const predefinedTasks = [
    { value: "update_address", label: "Update Address" },
    { value: "sell_new_product", label: "Try to Sell New Product" },
    { value: "collect_more_data", label: "Collect More Data" },
    { value: "new_dose", label: "Resell" },
    { value: "custom", label: "Custom Task" },
  ];

  const { t } = useLanguage();
  
  const handleConfirm = () => {
    const task = selectedTask === "custom" ? customTask.trim() : selectedTask;
    if (!task) {
      alert(t('leads.selectTask'));
      return;
    }
    onConfirm(task);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('leads.selectTask')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {t('leads.taskDescription')}
          </p>

          <div className="space-y-2">
            {predefinedTasks.map((task) => (
              <label
                key={task.value}
                className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name="task"
                  value={task.value}
                  checked={selectedTask === task.value}
                  onChange={(e) => setSelectedTask(e.target.value)}
                  className="mr-3"
                />
                <span className="text-sm font-medium text-gray-900">
                  {t(`leads.task${task.value.charAt(0).toUpperCase() + task.value.slice(1).replace(/_/g, '')}`) || 
                   (task.value === 'update_address' ? t('leads.taskUpdateAddress') :
                    task.value === 'sell_new_product' ? t('leads.taskSellNewProduct') :
                    task.value === 'collect_more_data' ? t('leads.taskCollectMoreData') :
                    task.value === 'new_dose' ? t('leads.taskNewDose') :
                    task.value === 'custom' ? t('leads.taskCustom') : task.label)}
                </span>
              </label>
            ))}
          </div>

          {selectedTask === "custom" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('leads.taskCustom')}
              </label>
              <textarea
                value={customTask}
                onChange={(e) => setCustomTask(e.target.value)}
                placeholder={t('leads.taskCustomPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
              />
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329]"
            >
              {t('leads.sendToCallCenter')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add/Edit Lead Modal Component
interface AddEditLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  lead?: Lead;
}

const AddEditLeadModal: React.FC<AddEditLeadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  lead,
}) => {
  // BEX dispatch removed for Greek-CC
  // const dispatch = useAppDispatch();
  
  // BEX Address data removed for Greek-CC (Greece uses ACS only)
  const municipalities: Municipality[] = [];
  const settlements: Settlement[] = [];
  const streets: Street[] = [];
  const bexDataLoaded = true;
  const bexDataLoading = false;

  const [formData, setFormData] = useState({
    name: lead?.name || "",
    phone: lead?.phone || "",
    email: lead?.email || "",
    address: "", // Street name
    addr: "", // House number / Apartment
    city: lead?.city || "",
    area: lead?.area || "",
    index: "", // Zip code
    notes: lead?.notes || "",
  });
  
  // BEX Address selection state
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<number | null>(null);
  const [selectedSettlementId, setSelectedSettlementId] = useState<number | null>(null);
  const [filteredSettlements, setFilteredSettlements] = useState<Settlement[]>([]);
  const [filteredStreets, setFilteredStreets] = useState<Street[]>([]);
  
  // Autocomplete states
  const [municipalitySearch, setMunicipalitySearch] = useState<string | null>(null);
  const [citySearch, setCitySearch] = useState<string | null>(null);
  const [streetSearch, setStreetSearch] = useState<string | null>(null);
  const [showMunicipalityDropdown, setShowMunicipalityDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showStreetDropdown, setShowStreetDropdown] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchingOrders, setMatchingOrders] = useState<any[]>([]);

  // Load BEX address data
  // BEX address loading removed for Greek-CC (Greece uses ACS only)
  // useEffect(() => {
  //   if (isOpen && !bexDataLoaded && !bexDataLoading) {
  //     dispatch(loadBexAddressData());
  //   }
  // }, [isOpen, bexDataLoaded, bexDataLoading, dispatch]);

  // Filter settlements when municipality changes
  useEffect(() => {
    if (selectedMunicipalityId) {
      const filtered = settlements.filter(s => s.municipalityId === selectedMunicipalityId);
      setFilteredSettlements(filtered);
    } else {
      setFilteredSettlements(settlements);
    }
  }, [selectedMunicipalityId, settlements]);

  // Filter streets when settlement changes
  useEffect(() => {
    if (selectedSettlementId) {
      const filtered = streets.filter(s => s.settlementId === selectedSettlementId);
      setFilteredStreets(filtered);
    } else {
      setFilteredStreets(streets);
    }
  }, [selectedSettlementId, streets]);

  // Helper function to extract street name and address number from address string
  const parseAddressComponents = (addressString: string): { street: string; number: string } => {
    if (!addressString) return { street: "", number: "" };
    
    // Remove leading/trailing whitespace
    let street = addressString.trim();
    let number = "";
    
    // First, detect and remove duplication patterns (e.g., "TREĆEG (3.) OKTOBRA 3.) OKTOBRA")
    // Check for repeated word sequences at the end of the string
    const words = street.split(/\s+/);
    
    if (words.length >= 4) {
      // Try to find the longest repeated suffix
      // Check from longest to shortest sequences
      for (let seqLength = Math.min(5, Math.floor(words.length / 2)); seqLength >= 1; seqLength--) {
        if (words.length < seqLength * 2) continue;
        
        const lastSequence = words.slice(-seqLength).join(' ');
        const beforeSequence = words.slice(0, -seqLength).join(' ');
        
        // Check if this sequence appears earlier in the string
        if (beforeSequence.includes(lastSequence)) {
          // Found duplication - remove the duplicate suffix
          street = beforeSequence.trim();
          break;
        }
      }
    }
    
    // Try to extract number patterns: " br. 5", " no 10", "#123", " 5/2", " 56"
    const numberPatterns = [
      /\s+(br\.?|no\.?|#)\s*(\d+.*)$/i,  // " br. 5", " no 10", "#123"
      /\s+(\d+\s*\/\s*\d+.*)$/i,         // " 5 / 2", " 10/20"
    ];
    
    for (const pattern of numberPatterns) {
      const match = street.match(pattern);
      if (match) {
        // Extract the number part (last capture group)
        number = match[match.length - 1].trim();
        // Remove the number from street name
        street = street.replace(pattern, '').trim();
        break;
      }
    }
    
    // Handle trailing standalone number (but not numbers in parentheses like "(3.)")
    if (!number) {
      // Match a trailing number that is NOT part of a pattern like "3.)" or "(3.)"
      // Look for: space + digits at the end, but not if preceded by ".)" or "("
      const trailingNumberPattern = /^(.+?)\s+(\d+)$/;
      const match = street.match(trailingNumberPattern);
      if (match) {
        const beforeNumber = match[1].trim();
        const potentialNumber = match[2];
        
        // Only extract if the part before doesn't end with ".)" or just ")"
        // This ensures we don't extract numbers that are part of "(3.)" patterns
        if (!beforeNumber.endsWith('.)') && 
            !beforeNumber.match(/\)\s*$/)) {
          // Additional check: make sure it's not part of a pattern like "WORD 3.)"
          const lastWord = beforeNumber.split(/\s+/).pop() || '';
          if (!lastWord.endsWith(')')) {
            number = potentialNumber;
            street = beforeNumber;
          }
        }
      }
    }
    
    return { street: street.trim(), number: number.trim() };
  };

  // Initialize form data from lead
  useEffect(() => {
    if (lead) {
      // Parse address - format is usually "area, city, street addr" or just "street addr"
      const addressParts = lead.address ? lead.address.split(", ") : [];
      
      // Get the last part which should contain street name and possibly address number
      const lastPart = addressParts.length > 0 ? addressParts[addressParts.length - 1] : "";
      
      // Extract street name and address number from the last part
      const { street, number } = parseAddressComponents(lastPart);
      
      // If street is empty, try the second-to-last part (in case format is "area, city, street, number")
      const streetName = street || (addressParts.length > 1 ? addressParts[addressParts.length - 2] : "") || lead.street || "";
      // Priority: lead.addr > lead.bexMatchDetails.addr > parsed number from address
      const addressNumber = lead.addr || lead.bexMatchDetails?.addr || number || "";
      
      setFormData({
        name: lead.name || "",
        phone: lead.phone || "",
        email: lead.email || "",
        address: streetName,
        addr: addressNumber,
        city: lead.city || "",
        area: lead.area || "",
        index: lead.index || "",
        notes: lead.notes || "",
      });
      setMatchingOrders(lead.matchingOrders || []);
      
      // Initialize street search
      if (streetName) {
        setStreetSearch(streetName);
      }
      
      // Initialize BEX address selections if municipalities are loaded
      if (municipalities.length > 0 && lead.area) {
        const municipality = municipalities.find(m => 
          m.name.toLowerCase() === (lead.area || '').toLowerCase()
        );
        if (municipality) {
          setSelectedMunicipalityId(municipality.id);
          setMunicipalitySearch(municipality.name);
        }
      }
    } else {
      // Reset form for new lead
      setFormData({
        name: "",
        phone: "",
        email: "",
        address: "",
        addr: "",
        city: "",
        area: "",
        index: "",
        notes: "",
      });
      setSelectedMunicipalityId(null);
      setSelectedSettlementId(null);
      setMunicipalitySearch(null);
      setCitySearch(null);
      setStreetSearch(null);
    }
  }, [lead, municipalities]);

  // Initialize settlement when city is set and municipality is selected
  useEffect(() => {
    if (formData.city && selectedMunicipalityId && filteredSettlements.length > 0) {
      const settlement = filteredSettlements.find(s => 
        s.name.toLowerCase() === formData.city.toLowerCase()
      );
      if (settlement) {
        setSelectedSettlementId(settlement.id);
        setCitySearch(settlement.name);
        if (!formData.index && settlement.zip) {
          setFormData(prev => ({ ...prev, index: settlement.zip || '' }));
        }
      }
    }
  }, [formData.city, selectedMunicipalityId, filteredSettlements]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.bex-address-container')) {
        setShowMunicipalityDropdown(false);
        setShowCityDropdown(false);
        setShowStreetDropdown(false);
      }
    };

    if (showMunicipalityDropdown || showCityDropdown || showStreetDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMunicipalityDropdown, showCityDropdown, showStreetDropdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      const url = lead
        ? `${API_BASE_URL}/api/leads/${lead.id}`
        : `${API_BASE_URL}/api/leads`;
      const method = lead ? "PUT" : "POST";

      // Build address string from BEX fields
      // Only use the street name from formData.address (not the full address)
      // If formData.address contains commas, it might be the full address, so extract just the street
      let streetName = formData.address || "";
      if (streetName.includes(", ")) {
        // If address contains commas, it's likely the full address, extract just the last part (street)
        const parts = streetName.split(", ");
        streetName = parts[parts.length - 1] || streetName;
      }
      
      // Remove any duplicates from streetName (e.g., "NOVI SAD, NOVI SAD" -> "NOVI SAD")
      const streetParts = streetName.split(", ").filter((part, index, arr) => {
        // Remove duplicates
        return arr.indexOf(part) === index;
      });
      streetName = streetParts.join(", ");

      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        address: streetName || null,
        city: formData.city || null,
        area: formData.area || null,
        index: formData.index || null,
        addr: formData.addr || null,
        notes: formData.notes || null,
      };

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to save lead");
      }

      const data = await response.json();
      if (data.success) {
        setMatchingOrders(data.data.matchingOrders || []);
        onSave();
      } else {
        throw new Error(data.error || "Failed to save lead");
      }
    } catch (err) {
      console.error("Error saving lead:", err);
      setError(err instanceof Error ? err.message : "Failed to save lead");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {lead ? "Edit Lead" : "Add New Lead"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                placeholder="e.g., 123456789"
              />
              <p className="text-xs text-gray-500 mt-1">
                Phone number without country code
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            />
          </div>

          {/* BEX Address Selection (for Serbia) */}
          <div className="space-y-4 bex-address-container">
            {/* Municipality (Area/Region) - Autocomplete */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-2" />
                Area/Region (Municipality)
              </label>
              <input
                type="text"
                value={municipalitySearch !== null ? municipalitySearch : (formData.area || "")}
                onChange={(e) => {
                  const value = e.target.value;
                  setMunicipalitySearch(value);
                  setShowMunicipalityDropdown(true);
                  
                  const municipality = municipalities.find(m => m.name === value);
                  if (municipality) {
                    setFormData(prev => ({ ...prev, area: municipality.name }));
                    setSelectedMunicipalityId(municipality.id);
                    setFormData(prev => ({ ...prev, city: "", address: "", index: "" }));
                    setSelectedSettlementId(null);
                    setCitySearch(null);
                    setStreetSearch(null);
                  } else {
                    setFormData(prev => ({ ...prev, area: value }));
                    setSelectedMunicipalityId(null);
                  }
                }}
                onFocus={() => {
                  setShowMunicipalityDropdown(true);
                  if (municipalitySearch === null && formData.area) {
                    setMunicipalitySearch(formData.area);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                placeholder="Type to search municipality..."
              />
              {showMunicipalityDropdown && municipalities.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {municipalities
                    .filter(m => m.name.toLowerCase().includes((municipalitySearch || "").toLowerCase()))
                    .slice(0, 20)
                    .map(municipality => (
                      <button
                        key={municipality.id}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, area: municipality.name, city: "", address: "", index: "" }));
                          setMunicipalitySearch(municipality.name);
                          setSelectedMunicipalityId(municipality.id);
                          setSelectedSettlementId(null);
                          setCitySearch(null);
                          setStreetSearch(null);
                          setShowMunicipalityDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                      >
                        {municipality.name}
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Settlement (City) - Autocomplete */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City (Settlement)
              </label>
              <input
                type="text"
                value={citySearch !== null ? citySearch : (formData.city || "")}
                onChange={(e) => {
                  const value = e.target.value;
                  setCitySearch(value);
                  setShowCityDropdown(true);
                  
                  const settlement = filteredSettlements.find(s => s.name === value);
                  if (settlement && selectedMunicipalityId && settlement.municipalityId === selectedMunicipalityId) {
                    setFormData(prev => ({ ...prev, city: settlement.name, index: settlement.zip || '', address: "" }));
                    setSelectedSettlementId(settlement.id);
                    setStreetSearch(null);
                  } else {
                    setFormData(prev => ({ ...prev, city: value }));
                    setSelectedSettlementId(null);
                  }
                }}
                onFocus={() => {
                  if (selectedMunicipalityId) {
                    setShowCityDropdown(true);
                    if (citySearch === null && formData.city) {
                      setCitySearch(formData.city);
                    }
                  }
                }}
                disabled={!selectedMunicipalityId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={selectedMunicipalityId ? "Type to search city..." : "Select municipality first"}
              />
              {!selectedMunicipalityId && (
                <p className="mt-1 text-xs text-gray-500">Select municipality first</p>
              )}
              {showCityDropdown && selectedMunicipalityId && filteredSettlements.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredSettlements
                    .filter(s => s.name.toLowerCase().includes((citySearch || "").toLowerCase()))
                    .slice(0, 20)
                    .map(settlement => (
                      <button
                        key={settlement.id}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, city: settlement.name, index: settlement.zip || '', address: "" }));
                          setCitySearch(settlement.name);
                          setSelectedSettlementId(settlement.id);
                          setStreetSearch(null);
                          setShowCityDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                      >
                        {settlement.name} ({settlement.zip})
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Zip Code (Auto-filled) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Zip Code
              </label>
              <input
                type="text"
                value={formData.index || ""}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                placeholder="Auto-filled from city"
              />
            </div>

            {/* Street - Autocomplete */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street
              </label>
              <input
                type="text"
                value={streetSearch !== null ? streetSearch : (formData.address || "")}
                onChange={(e) => {
                  const value = e.target.value;
                  setStreetSearch(value);
                  setShowStreetDropdown(true);
                  setFormData(prev => ({ ...prev, address: value }));
                }}
                onFocus={() => {
                  if (selectedSettlementId) {
                    setShowStreetDropdown(true);
                    if (streetSearch === null && formData.address) {
                      setStreetSearch(formData.address);
                    }
                  }
                }}
                disabled={!selectedSettlementId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={selectedSettlementId ? "Type to search street..." : "Select city first"}
              />
              {!selectedSettlementId && (
                <p className="mt-1 text-xs text-gray-500">Select city first</p>
              )}
              {showStreetDropdown && selectedSettlementId && filteredStreets.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredStreets
                    .filter(s => s.name.toLowerCase().includes((streetSearch || "").toLowerCase()))
                    .slice(0, 20)
                    .map(street => (
                      <button
                        key={street.id}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, address: street.name }));
                          setStreetSearch(street.name);
                          setShowStreetDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                      >
                        {street.name}
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* House Number / Apartment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Number
                <span className="text-xs text-gray-500 ml-1">(Format: 5 or 5/10 for apartment)</span>
              </label>
              <input
                type="text"
                value={formData.addr || ""}
                onChange={(e) =>
                  setFormData({ ...formData, addr: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                placeholder="e.g., 5 or 5/10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            />
          </div>

          {matchingOrders.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">
                Matching Historical Orders ({matchingOrders.length})
              </h3>
              <div className="space-y-1">
                {matchingOrders.map((order, idx) => (
                  <div
                    key={idx}
                    className="text-xs text-blue-800 flex items-center"
                  >
                    <Package className="w-3 h-3 mr-2" />
                    Order #{order.orderId} - Status: {order.status} -{" "}
                    {new Date(order.date).toLocaleDateString()}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : lead ? "Update Lead" : "Create Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Import type definitions ----
type ImportType = 'bex_order' | 'altercpa';

interface AvailableProduct {
  id: string;
  name: string;
  price: number;
  image: string | null;
}

// ---- Product Mapping Modal (shown when AlterCPA import has unmatched products) ----
interface ProductMappingModalProps {
  isOpen: boolean;
  unmatchedProducts: string[];
  matchedProducts: Array<{ csvName: string; productName: string }>;
  availableProducts: AvailableProduct[];
  onConfirm: (mappings: Record<string, string>) => void; // csvName → productId or 'skip'
  onClose: () => void;
}

const ProductMappingModal: React.FC<ProductMappingModalProps> = ({
  isOpen,
  unmatchedProducts,
  matchedProducts,
  availableProducts,
  onConfirm,
  onClose,
}) => {
  const [mappings, setMappings] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const allMapped = unmatchedProducts.every((p) => mappings[p]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Map Products to Database
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          <p className="text-sm text-gray-600">
            Some product names from the CSV could not be automatically matched to products in the database.
            Please map each one to the correct product, or skip it.
          </p>

          {/* Auto-matched products (info only) */}
          {matchedProducts.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm font-medium text-green-800 mb-1">
                Auto-matched ({matchedProducts.length})
              </p>
              <ul className="text-xs text-green-700 space-y-0.5">
                {matchedProducts.map((mp, i) => (
                  <li key={i}>
                    <span className="font-medium">{mp.csvName}</span> &rarr; {mp.productName}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Unmatched products - need user action */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-800">
              Unmatched products ({unmatchedProducts.length}) — select a match:
            </p>
            {unmatchedProducts.map((csvName) => (
              <div key={csvName} className="border border-orange-200 bg-orange-50 rounded-md p-3">
                <p className="text-sm font-semibold text-orange-900 mb-2">
                  &ldquo;{csvName}&rdquo;
                </p>
                <select
                  value={mappings[csvName] || ""}
                  onChange={(e) =>
                    setMappings((prev) => ({ ...prev, [csvName]: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e]"
                >
                  <option value="">-- Select a product --</option>
                  <option value="skip">Skip (do not set product)</option>
                  {availableProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.price > 0 ? `(${p.price.toLocaleString()} RSD)` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(mappings)}
            disabled={!allMapped}
            className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm & Import
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Import Results Modal (shown after import completes) ----
const ImportResultsModal: React.FC<{
  isOpen: boolean;
  result: any;
  onClose: () => void;
}> = ({ isOpen, result, onClose }) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[80vw]">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Import Results</h2>
        </div>
        <div className="p-5">
          {result && (
            <ImportResultDisplay
              result={result}
              onViewErrors={() => setShowErrorModal(true)}
            />
          )}
        </div>
        <div className="border-t border-gray-200 px-5 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329]"
          >
            Done
          </button>
        </div>
      </div>
      {result?.errors && result.errors.length > 0 && (
        <ImportErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          errors={result.errors}
          totalRows={result.total}
        />
      )}
    </div>
  );
};

// ---- Import Result Display (content for results modal) ----
const ImportResultDisplay: React.FC<{
  result: any;
  onViewErrors: () => void;
}> = ({ result, onViewErrors }) => {
  if (!result) return null;

  const hasErrors = result.errors && result.errors.length > 0;
  const hasDuplicates = result.duplicatesCount != null && result.duplicatesCount > 0;
  const duplicatedList = result.duplicatedList || [];
  const variant = hasErrors && result.created === 0
    ? 'warning'
    : result.created > 0
    ? 'success'
    : 'error';

  const colors = {
    warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: 'text-yellow-500' },
    success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: 'text-green-500' },
    error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-500' },
  }[variant];

  return (
    <div className={`border rounded-lg p-3 ${colors.bg} ${colors.border}`}>
      <div className="flex items-center gap-2 mb-3">
        {variant === 'success' ? (
          <CheckCircle className={`w-4 h-4 ${colors.icon} flex-shrink-0`} />
        ) : (
          <AlertCircle className={`w-4 h-4 ${colors.icon} flex-shrink-0`} />
        )}
        <span className={`text-xs font-medium ${colors.text}`}>Import results</span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <span>Total: <strong className="text-gray-800">{result.total}</strong></span>
        <span>Created: <strong className="text-green-700">{result.created}</strong></span>
        <span>Skipped: <strong className="text-gray-800">{result.skipped}</strong></span>
        {hasDuplicates && (
          <span>Duplicates: <strong className="text-amber-700">{result.duplicatesCount}</strong></span>
        )}
      </div>
      {hasDuplicates && duplicatedList.length > 0 && (() => {
        const firstWithRaw = duplicatedList.find((d: any) => d.rawRow && Object.keys(d.rawRow).length > 0);
        const columns = firstWithRaw
          ? ['row', ...Object.keys(firstWithRaw.rawRow)]
          : ['row', 'name', 'phone'];
        const getCellValue = (dup: any, col: string) => {
          if (col === 'row') return dup.row;
          if (dup.rawRow && col in dup.rawRow) {
            const v = dup.rawRow[col];
            return v != null && v !== '' ? String(v) : '—';
          }
          if (col === 'name') return dup.newLead?.name ?? '—';
          if (col === 'phone') return formatPhoneWithLeadingZero(dup.phone);
          return '—';
        };
        return (
          <div className="mt-2 w-full">
            <p className="text-xs font-medium text-amber-800 mb-1.5">Duplicates by phone</p>
            <div className="overflow-x-auto overflow-y-auto max-h-[40vh] rounded border border-gray-200 bg-white w-full">
              <table className="min-w-full text-xs w-full">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    {columns.map((col) => (
                      <th key={col} className="px-2 py-1.5 text-left font-medium text-gray-600 whitespace-nowrap">
                        {col === 'row' ? 'Row' : col.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {duplicatedList.map((dup: any, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      {columns.map((col) => (
                        <td key={col} className={`px-2 py-1 ${col === 'phone' ? 'font-mono' : ''} text-gray-800`}>
                          {getCellValue(dup, col)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
      {hasErrors && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">{result.errors.length} error{result.errors.length !== 1 ? 's' : ''}</span>
            <button
              onClick={onViewErrors}
              className="text-xs px-2 py-1 text-[#264a3e] hover:bg-[#264a3e]/10 rounded font-medium"
            >
              <Eye className="w-3 h-3 inline mr-1" />
              Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Import CSV Modal Component ----
interface ImportCSVModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: () => void;
  onSuccess?: (result: any) => void;
}

const ImportCSVModal: React.FC<ImportCSVModalProps> = ({
  isOpen,
  onClose,
  onImport,
  onSuccess,
}) => {
  const [importType, setImportType] = useState<ImportType>('bex_order');
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // AlterCPA product mapping
  const [productMappingData, setProductMappingData] = useState<any>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
      setProductMappingData(null);
    }
  };

  const handleImport = async (productMappings?: Record<string, string>) => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("file", file);

      // Determine endpoint based on import type
      const endpoint = importType === 'altercpa'
        ? `${API_BASE_URL}/api/leads/altercpa-import`
        : `${API_BASE_URL}/api/leads/bulk-import`;

      // For AlterCPA: attach product mappings if provided (second call after merge screen)
      if (importType === 'altercpa' && productMappings) {
        formData.append("productMappings", JSON.stringify(productMappings));
      }

      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to import leads");
      }

      const data = await response.json();

      // AlterCPA: needs product mapping
      if (data.needsProductMapping) {
        setProductMappingData(data);
        return;
      }

      if (data.success) {
        onImport();
        if (onSuccess) {
          onSuccess(data.data);
          onClose();
        }
      } else {
        throw new Error(data.error || "Failed to import leads");
      }
    } catch (err) {
      console.error("Error importing leads:", err);
      setError(err instanceof Error ? err.message : "Failed to import leads");
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  const importTypeLabels: Record<ImportType, { name: string; description: string }> = {
    bex_order: {
      name: "BEX / Order Import",
      description: "Import leads with address, orders, and BEX delivery data. Supports BEX and simple CSV formats."
    },
    altercpa: {
      name: "AlterCPA Import",
      description: "Import leads with name, phone, offer, and call history from AlterCPA. Supports pipe-separated multi-call records."
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Import Leads from CSV
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Import Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Import Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {(Object.keys(importTypeLabels) as ImportType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setImportType(type);
                    setError(null);
                    setProductMappingData(null);
                  }}
                  className={`text-left border-2 rounded-lg p-3 transition-all ${
                    importType === type
                      ? 'border-[#264a3e] bg-[#264a3e]/5 ring-1 ring-[#264a3e]/20'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className={`text-sm font-semibold ${importType === type ? 'text-[#264a3e]' : 'text-gray-800'}`}>
                    {importTypeLabels[type].name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {importTypeLabels[type].description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Expected format hint */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-xs font-medium text-gray-600 mb-1">Expected CSV columns:</p>
            {importType === 'bex_order' ? (
              <p className="text-xs text-gray-500">
                <code className="bg-gray-200 px-1 rounded">BEX_Primalac</code> (or name/firstname+lastname),{' '}
                <code className="bg-gray-200 px-1 rounded">EXCEL_telephone</code> (or phone),{' '}
                <code className="bg-gray-200 px-1 rounded">BEX_Street_Name_BEX</code>,{' '}
                <code className="bg-gray-200 px-1 rounded">BEX_Municipality_BEX</code>,{' '}
                <code className="bg-gray-200 px-1 rounded">WC_Products</code>, etc.
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                <code className="bg-gray-200 px-1 rounded">full_name</code>,{' '}
                <code className="bg-gray-200 px-1 rounded">phone</code>,{' '}
                <code className="bg-gray-200 px-1 rounded">offername</code>,{' '}
                <code className="bg-gray-200 px-1 rounded">last_call</code>,{' '}
                <code className="bg-gray-200 px-1 rounded">status</code>,{' '}
                <code className="bg-gray-200 px-1 rounded">reason</code>
                <br />
                <span className="text-gray-400 italic">offername, last_call, status, reason support pipe-separated (|) multi-values</span>
              </p>
            )}
          </div>

          {/* File input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#264a3e] file:text-white hover:file:bg-[#1a3329]"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => handleImport()}
              disabled={!file || importing}
              className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Leads
                </>
              )}
            </button>
          </div>
      </div>
    </div>

      {/* Product Mapping Modal (AlterCPA - unmatched products) */}
      {productMappingData && (
        <ProductMappingModal
          isOpen={true}
          unmatchedProducts={productMappingData.unmatchedProducts || []}
          matchedProducts={productMappingData.matchedProducts || []}
          availableProducts={productMappingData.availableProducts || []}
          onConfirm={async (mappings) => {
            setProductMappingData(null);
            // Re-import with the product mappings
            await handleImport(mappings);
          }}
          onClose={() => setProductMappingData(null)}
        />
      )}

    </div>
  );
};

export default LeadsMain;
